import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2, ShoppingCart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { formatProductPrice, getProductImage, getProductPath, getProducts } from '../services/productCatalog';

export default function ProductDetail() {
  const { productSlug } = useParams();
  const navigate = useNavigate();
  const { lang, t } = useLanguage();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [truckType, setTruckType] = useState('standard');
  const [volume, setVolume] = useState(5);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    let active = true;
    getProducts()
      .then((products) => {
        if (!active) return;
        const selected = products.find((item) => String(item.id) === productSlug || item.sku === productSlug);
        if (!selected) throw new Error('Produk tidak ditemukan atau sudah tidak aktif.');
        setProduct(selected);
        setRelatedProducts(products.filter((item) => item.id !== selected.id).slice(0, 3));
        setVolume(Number(selected.minimum_order_standard) || 5);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [productSlug]);

  const changeTruckType = (nextTruckType) => {
    setTruckType(nextTruckType);
    if (!product) return;
    const minimum = nextTruckType === 'minimix'
      ? Number(product.minimum_order_minimix) || 3
      : Number(product.minimum_order_standard) || 5;
    setVolume(minimum);
  };

  const addProduct = (checkoutNow = false) => {
    addToCart(product, volume, truckType);
    setAdded(true);
    if (checkoutNow) navigate('/cart');
  };

  if (loading) return <div className="product-detail-state">Memuat detail produk...</div>;
  if (error || !product) return (
    <div className="product-detail-state error">
      <h1>Produk tidak tersedia</h1>
      <p>{error}</p>
      <Link to="/produk-layanan" className="btn btn-primary">Kembali ke katalog</Link>
    </div>
  );

  const minVolume = truckType === 'minimix'
    ? Number(product.minimum_order_minimix) || 3
    : Number(product.minimum_order_standard) || 5;
  const estimatedSubtotal = Number(product.price_per_m3 || 0) * Number(volume || 0);

  return (
    <div className="master-product-detail">
      <div className="breadcrumb-wrapper">
        <div className="container">
          <ul className="breadcrumb-list">
            <li><Link to="/">{t('nav_home')}</Link></li>
            <li><span className="separator">/</span></li>
            <li><Link to="/produk-layanan">{t('nav_products')}</Link></li>
            <li><span className="separator">/</span></li>
            <li><span className="current">{product.name}</span></li>
          </ul>
        </div>
      </div>

      <section className="product-detail-main">
        <div className="container product-detail-grid">
          <div className="detail-image-panel">
            <img src={getProductImage(product)} alt={product.name} />
            {product.strength_class && product.strength_class !== 'N/A' && <span>{product.strength_class}</span>}
          </div>

          <div className="detail-info-panel">
            <div className="detail-category">{product.category} · {product.sku}</div>
            <h1>{product.name}</h1>
            <p className="detail-description">{product.description}</p>

            <div className="detail-spec-grid">
              <div><small>Mutu</small><strong>{product.strength_class || 'Sesuai spesifikasi'}</strong></div>
              <div><small>Slump standar</small><strong>{product.slump_default || '12 ± 2 cm'}</strong></div>
              <div><small>Minimum order</small><strong>{minVolume} m³</strong></div>
              <div><small>Harga per m³</small><strong>{formatProductPrice(product.price_per_m3, lang === 'id' ? 'id-ID' : 'en-US')}</strong></div>
            </div>

            {product.use_case && (
              <div className="use-case-box">
                <h2>Rekomendasi penggunaan</h2>
                <div>
                  {product.use_case.split(',').map((item) => (
                    <span key={item}><CheckCircle2 size={15} /> {item.trim()}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="order-configurator">
              <h2>Atur pesanan</h2>
              <div className="order-fields">
                <label>
                  Jenis armada
                  <select value={truckType} onChange={(event) => changeTruckType(event.target.value)}>
                    {product.is_standard_truck_available !== 0 && <option value="standard">Truck Standard</option>}
                    {product.is_minimix_available !== 0 && <option value="minimix">MiniMix</option>}
                  </select>
                </label>
                <label>
                  Volume (m³)
                  <input
                    type="number"
                    min={minVolume}
                    step="0.5"
                    value={volume}
                    onChange={(event) => setVolume(Math.max(minVolume, Number(event.target.value)))}
                  />
                </label>
              </div>
              <div className="estimated-total">
                <span>Estimasi subtotal</span>
                <strong>{estimatedSubtotal > 0 ? `Rp ${estimatedSubtotal.toLocaleString(lang === 'id' ? 'id-ID' : 'en-US')}` : 'Dikonfirmasi tim sales'}</strong>
              </div>
              <div className="detail-order-actions">
                {added ? (
                  <Link to="/cart" className="cart-secondary-button"><ShoppingCart size={17} /> Lihat Keranjang</Link>
                ) : (
                  <button onClick={() => addProduct(false)} className="cart-secondary-button"><ShoppingCart size={17} /> Tambah ke Keranjang</button>
                )}
                <button onClick={() => addProduct(true)} className="checkout-primary-button">
                  Checkout Sekarang <ArrowRight size={17} />
                </button>
              </div>
              <p className="price-disclaimer">Harga merupakan estimasi awal. Biaya pengiriman dan pompa dikonfirmasi berdasarkan lokasi proyek.</p>
            </div>
          </div>
        </div>
      </section>

      {relatedProducts.length > 0 && (
        <section className="related-master-products">
          <div className="container">
            <h2>Produk lainnya</h2>
            <div className="related-product-grid">
              {relatedProducts.map((item) => (
                <Link to={getProductPath(item)} className="related-product" key={item.id}>
                  <img src={getProductImage(item)} alt={item.name} />
                  <div><small>{item.category}</small><strong>{item.name}</strong></div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <style>{`
        .product-detail-state { min-height: 60vh; padding: 100px 24px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 16px; text-align: center; }
        .product-detail-state.error h1 { color: #b42318; }
        .product-detail-main { padding: 72px 0 92px; }
        .product-detail-grid { display: grid; grid-template-columns: minmax(0, 1.05fr) minmax(400px, .95fr); gap: 70px; align-items: start; }
        .detail-image-panel { position: sticky; top: 126px; height: 620px; overflow: hidden; background: #ececec; }
        .detail-image-panel img { width: 100%; height: 100%; object-fit: cover; }
        .detail-image-panel > span { position: absolute; top: 22px; left: 22px; padding: 10px 14px; background: #bbd448; color: #252525; font-size: 12px; font-weight: 800; }
        .detail-category { margin-bottom: 14px; color: #777654; font-size: 11px; font-weight: 800; letter-spacing: .7px; text-transform: uppercase; }
        .detail-info-panel > h1 { margin-bottom: 20px; color: #1f1f1f; font-size: 42px; line-height: 1.2; font-weight: 700; }
        .detail-description { margin-bottom: 30px; color: #5d5d5d; font-size: 17px; line-height: 1.7; }
        .detail-spec-grid { display: grid; grid-template-columns: repeat(2, 1fr); margin-bottom: 32px; border-top: 1px solid #deded8; border-left: 1px solid #deded8; }
        .detail-spec-grid > div { min-height: 86px; padding: 17px; border-right: 1px solid #deded8; border-bottom: 1px solid #deded8; }
        .detail-spec-grid small { display: block; margin-bottom: 5px; color: #777; font-size: 10px; text-transform: uppercase; }
        .detail-spec-grid strong { color: #292929; font-size: 14px; }
        .use-case-box { margin-bottom: 30px; }
        .use-case-box h2,
        .order-configurator h2 { margin-bottom: 14px; font-size: 18px; }
        .use-case-box > div { display: flex; flex-wrap: wrap; gap: 9px; }
        .use-case-box span { display: inline-flex; align-items: center; gap: 6px; padding: 8px 10px; background: #f4f4ef; color: #555; font-size: 12px; }
        .order-configurator { padding: 26px; background: #f4f4ef; border-left: 4px solid #bbd448; }
        .order-fields { display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px; }
        .order-fields label { color: #555; font-size: 11px; font-weight: 700; }
        .order-fields select,
        .order-fields input { width: 100%; height: 46px; margin-top: 7px; padding: 0 12px; border: 1px solid #d5d5cf; background: #fff; color: #333; font: inherit; }
        .estimated-total { margin: 20px 0; padding-top: 18px; border-top: 1px solid #d8d8d1; display: flex; align-items: center; justify-content: space-between; gap: 12px; }
        .estimated-total span { color: #666; font-size: 12px; }
        .estimated-total strong { font-size: 20px; }
        .detail-order-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        .cart-secondary-button,
        .checkout-primary-button { min-height: 48px; padding: 10px 14px; display: inline-flex; align-items: center; justify-content: center; gap: 8px; font-size: 12px; font-weight: 800; }
        .cart-secondary-button { border: 1px solid #777654; background: #fff; color: #777654; }
        .checkout-primary-button { background: #777654; color: #fff; }
        .checkout-primary-button:hover { background: #65644a; }
        .price-disclaimer { margin-top: 13px; color: #777; font-size: 10px; line-height: 1.5; }
        .related-master-products { padding: 70px 0 90px; background: #f4f4ef; }
        .related-master-products h2 { margin-bottom: 28px; font-size: 30px; }
        .related-product-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
        .related-product { display: grid; grid-template-columns: 116px 1fr; background: #fff; }
        .related-product img { width: 116px; height: 105px; object-fit: cover; }
        .related-product div { padding: 16px; display: flex; flex-direction: column; justify-content: center; }
        .related-product small { margin-bottom: 5px; color: #777654; font-size: 9px; text-transform: uppercase; }
        .related-product strong { font-size: 13px; line-height: 1.35; }
        @media (max-width: 900px) {
          .product-detail-grid { grid-template-columns: 1fr; gap: 38px; }
          .detail-image-panel { position: relative; top: 0; height: 460px; }
          .related-product-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 520px) {
          .product-detail-main { padding: 44px 0 64px; }
          .detail-image-panel { height: 340px; }
          .detail-spec-grid,
          .order-fields,
          .detail-order-actions { grid-template-columns: 1fr; }
          .order-configurator { padding: 20px; }
          .detail-info-panel > h1 { font-size: 22px; }
          .detail-description { font-size: 15px; }
        }
      `}</style>
    </div>
  );
}
